import { Link } from '@tanstack/react-router';
import { GitBranchIcon } from 'lucide-react';

import type { SubtaskParentRef } from '@/features/tasks';
import { runPageLink } from '@/features/workspace-overview/links';

/**
 * Whose subtask a row is: "Release 1.4 › Design reviews" (spec 150 FR-005).
 *
 * Inline, at the head of the row's meta line where the workflow's name would
 * otherwise be: the parent and the step that created this task say more than
 * the subtask's own workflow name, and the row keeps its two-line shape. The
 * branch mark is the board's "Subtask" badge's, so the two surfaces mark a
 * subtask the same way.
 *
 * A link above the row's stretched link (`relative z-10`), so choosing the
 * crumb opens the parent's run page rather than the row's own target.
 */
export function SubtaskCrumb({
  workspaceId,
  parent,
}: {
  workspaceId: string;
  parent: SubtaskParentRef;
}) {
  const mark = (
    <GitBranchIcon
      aria-hidden="true"
      // Inline, on the cap band rather than the baseline — the alignment
      // `StepKindIcon` settles for the same sentence.
      className="me-1 inline-block size-3 shrink-0 align-[-0.15em]"
      strokeWidth={1.75}
    />
  );

  if (!parent.reachable) {
    return (
      <span>
        {mark}
        Subtask
      </span>
    );
  }

  const text = parent.stepLabel
    ? `${parent.title} › ${parent.stepLabel}`
    : parent.title;
  return (
    <span>
      {mark}
      <Link
        {...runPageLink(workspaceId, parent.workflowKey, parent.taskId)}
        aria-label={
          parent.stepLabel
            ? `Subtask of ${parent.title}, ${parent.stepLabel}`
            : `Subtask of ${parent.title}`
        }
        title={text}
        className="hover:text-foreground focus-visible:ring-ring relative z-10 rounded-sm underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none"
      >
        {text}
      </Link>
    </span>
  );
}
